import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Button from '../components/Button.jsx';
import { FaClipboardList, FaUser, FaCalendarAlt, FaPalette, FaCheckCircle, FaLightbulb, FaMagic, FaLink, FaFacebook, FaImage, FaMusic, FaInfoCircle, FaQuestionCircle } from 'react-icons/fa';
import '../styles/Book.css';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

const ALL_TEMPLATES = [
  { id: 1, title: 'Classic Elegance', category: 'Wedding', price: '₱200' },
  { id: 2, title: 'Modern Romance', category: 'Wedding', price: '₱250' },
  { id: 3, title: 'Rustic Garden', category: 'Wedding', price: '₱300' },
  { id: 4, title: 'Kids Party Fun', category: 'Birthday', price: '₱200' },
  { id: 5, title: 'Elegant Milestone', category: 'Birthday', price: '₱250' },
  { id: 6, title: 'Fiesta Theme', category: 'Birthday', price: '₱200' },
  { id: 7, title: 'Angelic White', category: 'Christening', price: '₱200' },
  { id: 8, title: 'Blessed Journey', category: 'Christening', price: '₱250' },
  { id: 9, title: 'Professional Blue', category: 'Corporate', price: '₱200' },
  { id: 10, title: 'Executive Gold', category: 'Corporate', price: '₱300' },
  { id: 11, title: 'Silver Anniversary', category: 'Anniversary', price: '₱250' },
  { id: 12, title: 'Golden Years', category: 'Anniversary', price: '₱300' },
];

const Book = () => {
  const [searchParams] = useSearchParams();
  const pageTopRef = React.useRef(null);
  
  // Get template info from URL params
  const templateId = searchParams.get('template');
  const templateName = searchParams.get('name');
  const templateCategory = searchParams.get('category');
  const templatePrice = searchParams.get('price');
  const isCustomRequest = searchParams.get('type') === 'custom';

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    designType: isCustomRequest ? 'custom' : (templateId ? 'template' : ''),
    selectedTemplate: templateId ? `${templateName} (${templateCategory})` : '',
    eventType: templateCategory || '',
    eventDate: '',
    eventTime: '',
    venue: '',
    venueMapLink: '',
    facebookAccount: '',
    theme: '',
    colorPreference: '',
    referenceDesign: '',
    // Additional Information
    celebrantName: '',
    parentsNames: '',
    welcomeMessage: '',
    dressCode: '',
    rsvpEmail: '',
    rsvpDeadline: '',
    guestReminders: '',
    giftGuide: '',
    musicTitle: '',
    drivePhotosLink: '',
    driveMusicLink: '',
    specialInstructions: '',
    howDidYouHear: '',
    agreedToTerms: false
  });



  // Update form when URL params change
  useEffect(() => {
    if (templateId && templateName) {
      setFormData(prev => ({
        ...prev,
        designType: 'template',
        selectedTemplate: `${templateName} (${templateCategory})`,
        eventType: templateCategory || prev.eventType
      }));
    } else if (isCustomRequest) {
      setFormData(prev => ({
        ...prev,
        designType: 'custom',
        selectedTemplate: ''
      }));
    }
  }, [templateId, templateName, templateCategory, isCustomRequest]);

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('')

    // Required fields
    if (!formData.designType) {
      setErrorMessage('Please select a design type — Template or Custom.')
      return
    }
    if (!formData.fullName || !formData.email || !formData.phone || !formData.eventType || !formData.eventDate) {
      setErrorMessage('Please provide your name, email, phone, event type and event date.')
      return
    }
    if (!formData.celebrantName) {
      setErrorMessage('Please provide the celebrant name/title to be displayed on the invitation.')
      return
    }

    // Philippine phone number validation (09XXXXXXXXX or +639XXXXXXXXX)
    const phPhoneRegex = /^(09|\+639)\d{9}$/
    if (!phPhoneRegex.test(formData.phone.replace(/\s|-/g, ''))) {
      setErrorMessage('Please enter a valid Philippine phone number (e.g. 09XX XXX XXXX).')
      return
    }

    // Event date must not be in the past
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const selectedDate = new Date(formData.eventDate)
    if (selectedDate < today) {
      setErrorMessage('Event date cannot be in the past. Please select a future date.')
      return
    }

    // RSVP deadline must be on or before event date (only if provided)
    if (formData.rsvpDeadline) {
      const rsvpDate = new Date(formData.rsvpDeadline)
      if (!isNaN(rsvpDate.getTime()) && rsvpDate > selectedDate) {
        setErrorMessage('RSVP deadline must be on or before the event date.')
        return
      }
    }

    // Only validate RSVP email if provided
    if (formData.rsvpEmail && !/^\S+@\S+\.\S+$/.test(formData.rsvpEmail)) {
      setErrorMessage('Please enter a valid RSVP email address.')
      return
    }

    // Terms agreement check
    if (!formData.agreedToTerms) {
      setErrorMessage('Please agree to the Terms & Conditions before submitting.')
      return
    }

    setLoading(true)
    try {
      const payload = {
        ...formData,
        templateId: templateId || '',
        templateName: templateName || formData.selectedTemplate || '',
        templateCategory: templateCategory || '',
        templatePrice: templatePrice || ''
      }

      const resp = await fetch(`${API_BASE_URL}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}))
        throw new Error(data.message || "Sorry, we couldn't send your request. Please try again later.")
      }

      setSubmitted(true)
      // Scroll to top so user sees the success message
      window.scrollTo({ top: 0, behavior: 'smooth' })
      if (pageTopRef.current) {
        pageTopRef.current.scrollIntoView({ behavior: 'smooth' })
      }
    } catch (err) {
      setErrorMessage(err.message || "Sorry, we couldn't send your request. Please try again later.")
    } finally {
      setLoading(false)
    }
  };

  if (submitted) {
    return (
      <div id="booknow-page" ref={pageTopRef}>
        <div className="success-message">
          <div className="success-icon"><FaCheckCircle /></div>
          <h2>Thank you — we received your booking request.</h2>
          <p>Our sales team will contact you within 24 hours to discuss your e-invitation and confirm pricing.</p>
          <div className="success-details">
            <p><strong>What happens next?</strong></p>
            <ul>
                <li>📱 <strong>Step 1:</strong> Our team contacts you via Messenger within 24 hours</li>
                <li>💳 <strong>Step 2:</strong> We send payment details — pay 50% downpayment to start</li>
                <li>🎨 <strong>Step 3:</strong> We create your e-invitation based on your preferences</li>
                <li>👀 <strong>Step 4:</strong> You review the design and request any revisions</li>
                <li>✅ <strong>Step 5:</strong> Once approved, pay the remaining 50% balance</li>
                <li>🎉 <strong>Step 6:</strong> Receive your final e-invitation — share the love!</li>
                <li>⭐ <strong>Step 7:</strong> Leave feedback via Messenger or tag us in your post</li>
            </ul>
          </div>
          <Button variant="primary" onClick={() => setSubmitted(false)}>
            Submit Another Booking
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div id="booknow-page">
      <div className="book-header">
        <h1>Book Your E-Invitation</h1>
        <p>Fill out the form below and we'll create your perfect digital invitation</p>
        {formData.designType === 'template' && templateName && (
          <div className="selected-template-badge">
            <span><FaClipboardList /> Selected Template:</span>
            <strong>{templateName}</strong>
            <span className="template-price">{templatePrice}</span>
          </div>
        )}
        {formData.designType === 'custom' && (
            <div className="selected-template-badge custom">
            <span><FaMagic /> Custom Design Request</span>
            <span className="template-price">₱350 - ₱700</span>
          </div>
        )}
        {!formData.designType && (
          <div className="price-badge">
            <span className="price-label">Price Range:</span>
            <span className="price-value">₱200 - ₱700</span>
          </div>
        )}
      </div>

      <div className="booknow-container">
        <form className="book-form" onSubmit={handleSubmit}>
          {errorMessage && (
            <div className="form-error" role="alert" style={{ color: '#b00020', marginBottom: '12px' }}>
              {errorMessage}
            </div>
          )}
          {/* Design Type Selection */}
          <div className="form-section">
            <h2><FaPalette /> Design Type</h2>
            
            <div className="design-type-options">
              <label className={`design-type-card ${formData.designType === 'template' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="designType"
                  value="template"
                  checked={formData.designType === 'template'}
                  onChange={handleChange}
                />
                <div className="design-type-content">
                  <span className="design-type-icon"><FaClipboardList /></span>
                  <span className="design-type-title">Use a Template</span>
                  <span className="design-type-price">₱200 - ₱300</span>
                  <span className="design-type-desc">Choose from our ready-made designs</span>
                </div>
              </label>
              
              <label className={`design-type-card ${formData.designType === 'custom' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="designType"
                  value="custom"
                  checked={formData.designType === 'custom'}
                  onChange={handleChange}
                />
                <div className="design-type-content">
                  <span className="design-type-icon"><FaMagic /></span>
                  <span className="design-type-title">Custom Design</span>
                  <span className="design-type-price">₱350 - ₱700</span>
                  <span className="design-type-desc">Unique design based on your vision</span>
                </div>
              </label>
            </div>

            {formData.designType === 'template' && (
              <div className="form-group" style={{ marginTop: '1.5rem' }}>
                <label htmlFor="selectedTemplate">Selected Template *</label>
                <select
                  id="selectedTemplate"
                  name="selectedTemplate"
                  value={formData.selectedTemplate}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a template</option>
                  {ALL_TEMPLATES.map((t) => (
                    <option key={t.id} value={`${t.title} (${t.category})`}>
                      {t.title} — {t.category}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Personal Information */}
          <div className="form-section">
            <h2><FaUser /> Your Information</h2>
            
            <div className="form-group">
              <label htmlFor="fullName">Full Name *</label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Juan Dela Cruz"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="09XX XXX XXXX"
                  required
                />
              </div>
            </div>
          </div>

          {/* Event Details */}
          <div className="form-section">
            <h2><FaCalendarAlt /> Event Details</h2>
            
            <div className="form-group">
              <label htmlFor="eventType">Event Type *</label>
              <select
                id="eventType"
                name="eventType"
                value={formData.eventType}
                onChange={handleChange}
                required
              >
                <option value="">Select event type</option>
                <option value="wedding">Wedding</option>
                <option value="birthday">Birthday</option>
                <option value="christening">Christening / Baptism</option>
                <option value="anniversary">Anniversary</option>
                <option value="corporate">Corporate Event</option>
                <option value="debut">Debut (18th Birthday)</option>
                <option value="graduation">Graduation</option>
                <option value="baby-shower">Baby Shower</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="eventDate">Event Date *</label>
                <input
                  type="date"
                  id="eventDate"
                  name="eventDate"
                  value={formData.eventDate}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="eventTime">Event Time <span className="field-optional">(optional)</span></label>
                <input
                  type="time"
                  id="eventTime"
                  name="eventTime"
                  value={formData.eventTime}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="venue">Event Venue / Location <span className="field-optional">(optional)</span></label>
              <input
                type="text"
                id="venue"
                name="venue"
                value={formData.venue}
                onChange={handleChange}
                placeholder="e.g., Manila Hotel, BGC, etc."
              />
            </div>

            <div className="form-group">
              <label htmlFor="venueMapLink"><FaLink style={{marginRight:'0.4rem'}}/>Venue Map Link / Pinned Location <span className="field-optional">(optional)</span></label>
              <input
                type="url"
                id="venueMapLink"
                name="venueMapLink"
                value={formData.venueMapLink}
                onChange={handleChange}
                placeholder="https://maps.google.com/... or any pinned location URL"
              />
            </div>

            <div className="form-group">
              <label htmlFor="facebookAccount"><FaFacebook style={{marginRight:'0.4rem'}}/>Facebook Account <span className="field-optional">(optional)</span></label>
              <input
                type="text"
                id="facebookAccount"
                name="facebookAccount"
                value={formData.facebookAccount}
                onChange={handleChange}
                placeholder="e.g., facebook.com/yourprofile or @YourPage"
              />
            </div>
          </div>

          {/* Design Preferences */}
          <div className="form-section">
            <h2><FaPalette /> Design Preferences</h2>
            
            <div className="form-group">
              <label htmlFor="theme">Theme / Motif <span className="field-optional">(optional)</span></label>
              <input
                type="text"
                id="theme"
                name="theme"
                value={formData.theme}
                onChange={handleChange}
                placeholder="e.g., Rustic, Minimalist, Floral, Filipino, etc."
              />
            </div>

            <div className="form-group">
              <label htmlFor="colorPreference">Color Preference <span className="field-optional">(optional)</span></label>
              <input
                type="text"
                id="colorPreference"
                name="colorPreference"
                value={formData.colorPreference}
                onChange={handleChange}
                placeholder="e.g., Blush pink and gold, Navy blue and silver"
              />
            </div>

            <div className="form-group">
              <label htmlFor="referenceDesign">Reference Design <span className="field-optional">(optional)</span></label>
              <input
                type="text"
                id="referenceDesign"
                name="referenceDesign"
                value={formData.referenceDesign}
                onChange={handleChange}
                placeholder="Paste a link to Pinterest, Instagram, or our sample you like"
              />
            </div>
          </div>

          {/* Additional Information */}
          <div className="form-section">
            <h2><FaInfoCircle /> Additional Information</h2>

            <div className="form-group">
              <label htmlFor="celebrantName">Celebrant Name / Title (to be displayed on invitation) *</label>
              <input
                type="text"
                id="celebrantName"
                name="celebrantName"
                value={formData.celebrantName}
                onChange={handleChange}
                placeholder="e.g., Maria Santos, The Birthday Girl, etc."
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="parentsNames">Father's Name and Mother's Name <span className="field-optional">(optional)</span></label>
              <input
                type="text"
                id="parentsNames"
                name="parentsNames"
                value={formData.parentsNames}
                onChange={handleChange}
                placeholder="e.g., Juan Santos & Maria Santos"
              />
            </div>

            <div className="form-group">
              <label htmlFor="drivePhotosLink"><FaImage style={{marginRight:'0.4rem'}}/>Photos — Google Drive Link <span className="field-optional">(optional)</span></label>
              <input
                type="url"
                id="drivePhotosLink"
                name="drivePhotosLink"
                value={formData.drivePhotosLink}
                onChange={handleChange}
                placeholder="https://drive.google.com/drive/folders/..."
              />
              <small className="field-hint">Upload your cover photo and gallery photos to Google Drive, set sharing to <strong>"Anyone with the link"</strong>, then paste the folder link here.</small>
            </div>

            <div className="form-group">
              <label htmlFor="welcomeMessage">Short Welcome Message <span className="field-optional">(optional)</span></label>
              <textarea
                id="welcomeMessage"
                name="welcomeMessage"
                value={formData.welcomeMessage}
                onChange={handleChange}
                placeholder="e.g., Join us as we celebrate..."
                rows="3"
              />
            </div>

            <div className="form-group">
              <label htmlFor="dressCode">Dress Code Details <span className="field-optional">(optional)</span></label>
              <input
                type="text"
                id="dressCode"
                name="dressCode"
                value={formData.dressCode}
                onChange={handleChange}
                placeholder="e.g., Semi-formal, White and Gold attire"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="rsvpEmail">RSVP Email Address <span className="field-optional">(optional)</span></label>
                <input
                  type="email"
                  id="rsvpEmail"
                  name="rsvpEmail"
                  value={formData.rsvpEmail}
                  onChange={handleChange}
                  placeholder="rsvp@youremail.com"
                />
                <small className="field-hint">
                  Where RSVP responses will be sent.{' '}
                  {formData.email && formData.rsvpEmail !== formData.email && (
                    <button
                      type="button"
                      className="use-my-email-btn"
                      onClick={() => setFormData(prev => ({ ...prev, rsvpEmail: prev.email }))}
                    >
                      Use my email
                    </button>
                  )}
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="rsvpDeadline">RSVP Deadline <span className="field-optional">(optional)</span></label>
                <input
                  type="date"
                  id="rsvpDeadline"
                  name="rsvpDeadline"
                  value={formData.rsvpDeadline}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="guestReminders">Guest Reminders or FAQs <span className="field-optional">(optional)</span></label>
              <textarea
                id="guestReminders"
                name="guestReminders"
                value={formData.guestReminders}
                onChange={handleChange}
                placeholder="e.g., Please be seated by 5:30 PM. Parking is available at..."
                rows="3"
              />
            </div>

            <div className="form-group">
              <label htmlFor="giftGuide">Gift Guide <span className="field-optional">(optional)</span></label>
              <textarea
                id="giftGuide"
                name="giftGuide"
                value={formData.giftGuide}
                onChange={handleChange}
                placeholder="e.g., Cash gifts are appreciated. Registry at..."
                rows="3"
              />
            </div>

            <div className="form-section-sub">
              <p className="sub-section-title"><FaMusic style={{marginRight:'0.4rem'}}/>Preferred Background Music <span className="field-optional">(optional)</span></p>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="musicTitle">Music Title <span className="field-optional">(optional)</span></label>
                  <input
                    type="text"
                    id="musicTitle"
                    name="musicTitle"
                    value={formData.musicTitle}
                    onChange={handleChange}
                    placeholder="e.g., Perfect by Ed Sheeran"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="driveMusicLink"><FaMusic style={{marginRight:'0.4rem'}}/>Music File — Google Drive Link <span className="field-optional">(optional)</span></label>
                  <input
                    type="url"
                    id="driveMusicLink"
                    name="driveMusicLink"
                    value={formData.driveMusicLink}
                    onChange={handleChange}
                    placeholder="https://drive.google.com/file/d/..."
                  />
                  <small className="field-hint">Upload your audio file to Google Drive, set sharing to <strong>"Anyone with the link"</strong>, then paste the link here.</small>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="specialInstructions">Any other special instructions or requests? <span className="field-optional">(optional)</span></label>
              <textarea
                id="specialInstructions"
                name="specialInstructions"
                value={formData.specialInstructions}
                onChange={handleChange}
                placeholder="Additional details you want included in the invitation (e.g., names of guests, special notes...)"
                rows="4"
              />
            </div>
          </div>

          {/* How did you hear about us */}
          <div className="form-section">
            <h2><FaQuestionCircle /> How Did You Hear About Us?</h2>
            <div className="form-group">
              <label htmlFor="howDidYouHear">How did you hear about us? <span className="field-optional">(optional)</span></label>
              <select
                id="howDidYouHear"
                name="howDidYouHear"
                value={formData.howDidYouHear}
                onChange={handleChange}
              >
                <option value="">Select an option</option>
                <option value="facebook">Facebook</option>
                <option value="instagram">Instagram</option>
                <option value="google">Google Search</option>
                <option value="referral">Friend/Family Referral</option>
                <option value="website">Logica Technology Website</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="form-actions">
            <div className="form-note">
              <p><FaLightbulb /> <strong>Note:</strong> After submitting, our team will contact you within 24 hours to discuss your design and confirm the final price.</p>
              <p style={{marginTop: '0.5rem'}}>💳 <strong>Payment Terms:</strong> 50% downpayment upon booking. Remaining 50% is due once the design is completed and approved by you.</p>
            </div>

            <label className="terms-checkbox-label">
              <input
                type="checkbox"
                name="agreedToTerms"
                checked={formData.agreedToTerms}
                onChange={(e) => setFormData(prev => ({ ...prev, agreedToTerms: e.target.checked }))}
                required
              />
              <span>
                I have read and agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Notice</a>.
              </span>
            </label>
            <Button type="submit" variant="primary" size="large" disabled={loading}>
              {loading ? 'Sending your booking request…' : 'Submit Booking Request'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Book;
